import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import axiosClient from "../../services/apiClient";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as PieTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import AlertTrendsCard from "./AlertTrendsCard";
import AlertHeatmapCard from "./AlertHeatmapCard";
import ProvisioningQRModal from "../../components/ui/ProvisioningQRModal";
import { useInsightsCtx } from "./InsightsLayout";
import {
  getSensorAlarms,
  getSensorSessions,
  getSensorPresence,
  getSensorConnectivity,
  getSensorStateChanges,
  getPCOStateChanges,
  getBootInfo,
  getSensors,
  groupAlertsByType,
  listAlertTransitions,
  countAlarmShutoffs,
  deriveStoveControl,
} from "./unitEventsUtils";
import Spinner from "../../components/ui/Spinner";
import ErrorState from "../../components/ui/ErrorState";
import StatusDot from "../../components/ui/StatusDot";
import Button from "../../components/ui/Button";

const ALERT_LABEL: Record<string, string> = {
  isTooHot: "Dangerous temperature",
  isUnattended: "Unattended cooking",
  isImmediateDanger: "Immediate danger",
};

function formatAbsolute(ts: number): string {
  return new Date(ts).toLocaleString();
}

function StatCard({
  label,
  value,
  tone,
  title,
}: {
  label: string;
  value: number | string;
  tone?: "critical" | "warning" | "info" | "muted" | "success" | "fire";
  title?: string;
}) {
  const colorClass = {
    critical: "text-alert-critical",
    warning: "text-alert-warning",
    info: "text-alert-info",
    muted: "text-ink-3",
    success: "text-alert-success",
    fire: "text-alert-fire",
  }[tone || "muted"];
  return (
    <div
      className="flex-1 min-w-[120px] rounded-card border border-white/10 bg-white/5 p-4 text-center flex flex-col"
      title={title}
    >
      <div className="text-xs uppercase tracking-wide text-ink-3 min-h-[2.6em] flex items-start justify-center">
        {label}
      </div>
      <div className={`text-3xl font-bold mt-2 ${colorClass}`}>{value}</div>
    </div>
  );
}

export default function SensorDetailPage() {
  const { buildingId = "", sensorId: rawSensorId = "" } = useParams();
  const sensorId = decodeURIComponent(rawSensorId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [qrOpen, setQrOpen] = useState(false);
  const { buildings, startDate, endDate } = useInsightsCtx();

  const building = buildings.find((b) => b.buildingId === buildingId);

  // Sensors list for this building (cached if we just came from the sensors page).
  const {
    data: sensorListResp,
    isPending: sensorListLoading,
    error: sensorListError,
  } = useQuery({
    queryKey: ["sensors", buildingId],
    queryFn: async () => await getSensors(buildingId),
    enabled: !!buildingId,
    staleTime: 60_000,
  });
  const sensorList: { name: string; displayName?: string }[] =
    sensorListResp?.data ?? [];
  const sensor = sensorList.find((s) => s.name === sensorId);

  // Alerts (shared cache key with the aggregates hook + Level 1 table).
  const { data: alertsResp, isPending: alertsLoading } = useQuery({
    queryKey: ["alerts-agg", sensorId],
    queryFn: async () => await getSensorAlarms(sensorId, 5000),
    enabled: !!sensorId,
  });
  const alertRecords = useMemo(
    () => alertsResp?.data ?? [],
    [alertsResp]
  );

  // Trends chart history uses the same `alerts-agg` cache (now 5000 records).
  const TREND_HISTORY_PAGE_SIZE = 5000;
  const historyRecords = alertRecords;
  const trendHistoryFetching = alertsLoading;

  // Sessions — pull a window so we can show "last session" and compute the
  // presence ratio off the same payload. Server-filters when picker dates are set.
  const { data: sessionsResp, isPending: lastSessionLoading } = useQuery({
    queryKey: ["recent-sessions", sensorId, startDate, endDate],
    queryFn: async () =>
      await getSensorSessions(sensorId, 50, startDate, endDate),
    enabled: !!sensorId,
  });
  const sessions = useMemo(
    () => sessionsResp?.data ?? [],
    [sessionsResp]
  );

  // Larger session pull powering the trends chart's year/all-time views.
  const SESSIONS_HISTORY_PAGE_SIZE = 1000;
  const { data: sessionsHistoryResp } = useQuery({
    queryKey: ["sessions-history", sensorId],
    queryFn: async () =>
      await getSensorSessions(
        sensorId,
        SESSIONS_HISTORY_PAGE_SIZE,
        null,
        null
      ),
    enabled: !!sensorId,
    staleTime: 60_000,
  });
  const historySessions = useMemo(
    () => sessionsHistoryResp?.data ?? [],
    [sessionsHistoryResp]
  );

  const lastSession = sessions[0] ?? null;

  const { data: presenceResp } = useQuery({
    queryKey: ["last-presence", sensorId],
    queryFn: async () => await getSensorPresence(sensorId, 1),
    enabled: !!sensorId,
  });
  const lastPresenceTs = useMemo(() => {
    const row = presenceResp?.data?.[0];
    if (!row?.datetime) return null;
    const ts = Date.parse(String(row.datetime).replace(" ", "T"));
    return Number.isNaN(ts) ? null : ts;
  }, [presenceResp]);

  // Connectivity / state for the header.
  const { data: connResp } = useQuery({
    queryKey: ["connectivity", sensorId],
    queryFn: async () => await getSensorConnectivity(sensorId, 1),
    enabled: !!sensorId,
  });
  const { data: stateResp } = useQuery({
    queryKey: ["statechanges", sensorId],
    queryFn: async () => await getSensorStateChanges(sensorId, 1),
    enabled: !!sensorId,
  });
  const { data: pcoResp } = useQuery({
    queryKey: ["currentPCOstatechanges", sensorId],
    queryFn: async () => await getPCOStateChanges(sensorId, 1),
    enabled: !!sensorId,
  });
  const { data: bootResp } = useQuery({
    queryKey: ["bootinfo", sensorId],
    queryFn: async () => await getBootInfo(sensorId, 1),
    enabled: !!sensorId,
  });

  const isOnline = connResp?.data?.[0]?.eventType === "connected";
  const pcoState = pcoResp?.data?.[0];
  const stateRow = stateResp?.data?.[0];
  const stoveOn =
    stateRow &&
    stateRow.currentStateEnum !== 6 &&
    stateRow.currentStateEnum !== 9;
  // Match the Sensors-page detection: a PCO is attached whenever the latest
  // PCO state-change record exists and reports a non-zero connected status.
  // (Mobile's `checkShutdown` uses the same rule.)
  const pcoAttached =
    !!pcoState && pcoState.currentUnitConnectedStatus !== 0;
  const pcoLocked =
    pcoState && !pcoState.currentIsStoveRemoteUnlocked && !stoveOn;
  const scsFwRaw = bootResp?.data?.[0]?.firmwareVersion;
  const scsFw = scsFwRaw ? String(scsFwRaw).split("#")[0].trim() : null;
  const pcoFw = pcoState
    ? `${pcoState.currentFWverMajor}.${pcoState.currentFWverMinor}.${pcoState.currentFWverPatch}`
    : null;

  // Derived data, filtered by the page-level date range.
  const alertsByType = useMemo(
    () => groupAlertsByType(alertRecords, startDate, endDate),
    [alertRecords, startDate, endDate]
  );
  const shutoffCount = useMemo(
    () => countAlarmShutoffs(alertRecords, startDate, endDate),
    [alertRecords, startDate, endDate]
  );
  // "Last alert" should be truly last — independent of the picker window.
  const lastAlertEver = useMemo(
    () => listAlertTransitions(alertRecords, null, null)[0] ?? null,
    [alertRecords]
  );

  if (sensorListLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={96} />
      </div>
    );
  }

  if (sensorListError || !building) {
    return (
      <ErrorState
        title="Property not found"
        message="We couldn't load this property's sensors."
        onRetry={() => navigate("/insights")}
      />
    );
  }

  if (!sensor) {
    return (
      <ErrorState
        title="Sensor not found"
        message={`No sensor named "${sensorId}" in ${building.name}.`}
        onRetry={() => navigate(`/insights/properties/${buildingId}`)}
      />
    );
  }

  // Same Stove Control logic as the Sensors table — derives badge + action
  // from connectivity / PCO / stove / lock state.
  const ctrl = deriveStoveControl({
    Status: isOnline ? "Online" : "Offline",
    isPCOconnected: pcoAttached ? "YES" : "NO",
    stoveStatus: stoveOn ? "ON" : "OFF",
    IsStoveRemoteUnlocked: pcoLocked ? "LOCKED" : "UNLOCKED",
  });

  const refetchSensorState = () => {
    queryClient.invalidateQueries({ queryKey: ["statechanges", sensorId] });
    queryClient.invalidateQueries({
      queryKey: ["currentPCOstatechanges", sensorId],
    });
  };

  const handleShutoff = async () => {
    if (
      !window.confirm(
        `Shut off the stove on ${sensor.displayName || sensorId} now?`
      )
    ) {
      return;
    }
    await axiosClient.post(`sensors/${sensorId}/shutdownstove`);
    setTimeout(refetchSensorState, 5000);
  };

  const handleUnlock = async () => {
    await axiosClient.post(`sensors/${sensorId}/unlockstove`);
    setTimeout(refetchSensorState, 5000);
  };

  const onAction = () => {
    if (ctrl.action === "shutoff") handleShutoff();
    else if (ctrl.action === "unlock") handleUnlock();
  };

  return (
    <div className="space-y-6">
      {/* Top row: header (left) + recent activity (right) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-3xl font-semibold flex items-center gap-3">
              {sensor.displayName || sensorId}
              <StatusDot
                tone={isOnline ? "online" : "offline"}
                label={isOnline ? "Online" : "Offline"}
              />
            </div>
            <div className="text-ink-2 mt-1 text-sm">{building.name}</div>
            {isOnline && (
              <div className="mt-3" title={ctrl.tooltip}>
                {ctrl.action ? (
                  <Button
                    size="md"
                    variant={
                      ctrl.action === "shutoff" ? "danger" : "primary"
                    }
                    onClick={onAction}
                    className="!rounded-control !border-black whitespace-nowrap shadow-sm"
                  >
                    {ctrl.actionLabel}
                  </Button>
                ) : (
                  <StatusDot
                    tone={ctrl.tone as any}
                    label={ctrl.longLabel || ctrl.label}
                  />
                )}
              </div>
            )}
          </div>
          <div className="text-sm text-ink-2 text-right space-y-1">
            {scsFw && (
              <div>
                <span className="text-ink-3">SCS firmware:</span>{" "}
                <span className="font-medium">{scsFw}</span>
              </div>
            )}
            {pcoFw && (
              <div>
                <span className="text-ink-3">PCO firmware:</span>{" "}
                <span className="font-medium">{pcoFw}</span>
              </div>
            )}
            <div>
              <span className="text-ink-3">Sensor ID:</span>{" "}
              <span className="font-medium">{sensorId}</span>
            </div>
            <div className="pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQrOpen(true)}
                className="!text-ink !border-black/15 hover:!bg-black/[0.04]"
              >
                Show QR Code
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5">
          <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-3 text-sm">
            <dt className="text-ink-3">Last cooking session</dt>
            <dd className="font-medium">
              {lastSessionLoading
                ? "…"
                : !lastSession
                  ? "No sessions recorded"
                  : (() => {
                      const raw = lastSession.dateStart;
                      const ts = raw ? Date.parse(String(raw)) : NaN;
                      return Number.isNaN(ts) ? "—" : formatAbsolute(ts);
                    })()}
            </dd>
            <dt className="text-ink-3">Last presence detected</dt>
            <dd className="font-medium">
              {lastPresenceTs == null
                ? "Not detected"
                : formatAbsolute(lastPresenceTs)}
            </dd>
            <dt className="text-ink-3">Last alert</dt>
            <dd className="font-medium">
              {lastAlertEver
                ? `${ALERT_LABEL[lastAlertEver.type]} · ${formatAbsolute(
                    lastAlertEver.timestamp
                  )}`
                : "None recorded"}
            </dd>
          </dl>
        </div>
      </div>

      {/* Bottom row: combined Alerts (tiles + pie) on the left, trends on the right */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
        <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Alerts</h2>
            {alertsLoading && (
              <span className="text-ink-3 text-sm">Loading…</span>
            )}
          </div>
          <div className="flex flex-wrap gap-3 mb-6">
            <StatCard
              label="Unattended"
              value={alertsByType.isUnattended}
              tone="warning"
            />
            <StatCard
              label="Dangerous temperature"
              value={alertsByType.isTooHot}
              tone="fire"
            />
            <StatCard
              label="Immediate danger"
              value={alertsByType.isImmediateDanger}
              tone="critical"
            />
            <StatCard
              label="Auto shut-offs"
              value={pcoAttached ? shutoffCount : "N/A"}
              tone="info"
              title={
                pcoAttached
                  ? "Times the PCO cut power in response to a danger alarm during this window."
                  : "This sensor has no power cutoff unit attached."
              }
            />
          </div>
          <div className="flex-1 min-h-0">
            {(() => {
              const pieData = [
                {
                  name: "Unattended",
                  value: alertsByType.isUnattended,
                  color: "#FB8C00",
                },
                {
                  name: "Dangerous temperature",
                  value: alertsByType.isTooHot,
                  color: "#E74C3C",
                },
                {
                  name: "Immediate danger",
                  value: alertsByType.isImmediateDanger,
                  color: "#B00020",
                },
              ].filter((d) => d.value > 0);
              const total = pieData.reduce((a, d) => a + d.value, 0);
              if (total === 0) {
                return (
                  <div className="text-ink-3 text-sm text-center py-12">
                    No alerts in this window.
                  </div>
                );
              }
              return (
                <ResponsiveContainer width="100%" height="100%" minHeight={224}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {pieData.map((d) => (
                        <Cell key={d.name} fill={d.color} />
                      ))}
                    </Pie>
                    <PieTooltip
                      formatter={(value: number, name: string) => [
                        `${value} (${Math.round((value / total) * 100)}%)`,
                        name,
                      ]}
                    />
                    <Legend
                      verticalAlign="bottom"
                      iconType="circle"
                      wrapperStyle={{ fontSize: "12px" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              );
            })()}
          </div>
        </div>

        <AlertTrendsCard
          records={alertRecords}
          historyRecords={historyRecords}
          historyLoading={trendHistoryFetching}
          historyCap={TREND_HISTORY_PAGE_SIZE}
          sessions={sessions}
          historySessions={historySessions}
        />
      </div>

      <AlertHeatmapCard
        records={alertRecords}
        historyRecords={historyRecords}
        sessions={sessions}
        historySessions={historySessions}
      />

      <ProvisioningQRModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        sensorName={sensorId}
        displayName={sensor.displayName}
      />
    </div>
  );
}
